//react
import { useMemo, FC } from 'react';
//recoil
import { useRecoilValue, useSetRecoilState } from 'recoil';
//atoms
import { popupAtom } from '@atoms/popupAtom';
import { currentCMSAtom } from '@atoms/currentCMS';
//hooks
import useTablesLogic from '@hooks/tables/useTablesLogic';
import { useDeleteUser, useDeleteUsers } from '@api/mutations/user/user';
import { useInfinityUsers, useUsers } from '@api/queries/user/user';
import { useTranslation } from 'react-i18next';
import useLastElementScroll from '@hooks/lastElementScroll/useLastElementScroll';
//containers
import TableHeaderActions from '@containers/TableHeaderAction/TableHeaderActions';
import MobileTableHeader from '@containers/MobileTableHeader/MobileTableHeader';
import MobileMenu from '@containers/MobileMenu/MobileMenu';
//components
import Loading from '@components/Loading/Loading';
import MobileUserRow from '@components/MobileUserRow/MobileUserRow';
//interfaces
import { User } from '@interfaces/user/user';
import { ErrorResponse, SuccessResponse } from '@interfaces/apiResponse';
//layout
import { TableLayout } from '@layout/TableLayout/TableLayout';
//material-react-table
import { type MRT_ColumnDef } from 'material-react-table';
//mui
import {
  Grid,
  Card,
  useMediaQuery,
  Box,
  Alert,
  Checkbox,
  Typography,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
//theme
import theme from '@src/theme';
//icons
import { DeleteRowIcon } from '@components/icons/icons';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
//helpers
import { checkPermissionFunc } from '@helpers/utility';
//enums
import { PermissionsEnum } from '@enum/permissionsEnum';

const UsersPage: FC = (): JSX.Element | null => {
  const setPopup = useSetRecoilState(popupAtom);
  const currentCMS = useRecoilValue(currentCMSAtom);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onDeleteSuccesss = (data: SuccessResponse) => {
    setSuccessfullyDeletedPopup(true);
    !isMobile && refetch();
    isMobile && refetchInfinityUsers();
  };

  const onDeleteError = (err: ErrorResponse) => {
    setPopup({
      open: true,
      title: err.message,
      content: '',
      variant: 'error'
    });
  };

  const { mutate: deleteUser } = useDeleteUser(onDeleteSuccesss, onDeleteError);
  const { mutate: deleteUsers } = useDeleteUsers(onDeleteSuccesss, onDeleteError);

  const {
    pagination,
    setPagination,
    tableInstanceRef,
    handleSearch,
    sortBy,
    sortDirection,
    handleSortTables,
    checkSort,
    handleClearSort,
    setSuccessfullyDeletedPopup,
    handleSubmitFilter,
    filterValues,
    controlFilter,
    filterValue,
    handleClearFilter,
    anchorSortEl,
    anchorEl,
    anchorMenuItem,
    handleClick,
    handleClose,
    openMobileUserDropdown,
    handleToggleMobileDetails,
    successfullyDeletedPopup,
    deleteIds,
    setDeleteIds,
    deletedNames,
    deleteEventHandler,
    anchorElActionButton,
    openMobileMenu,
    setOpenMobileMenu
  } = useTablesLogic(deleteUser, deleteUsers, 'superAdminUsers');

  const {
    data,
    refetch,
    isLoading: isLoadingUsers
  } = useUsers(
    pagination.pageIndex + 1,
    pagination.pageSize,
    filterValues.search,
    filterValues.gender.toString(),
    sortBy,
    sortDirection,
    onUsersError
  );

  const {
    data: infinityUsers,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    refetch: refetchInfinityUsers
  } = useInfinityUsers(15, filterValues.search, sortBy, sortDirection, onUsersError);

  function onUsersError(err: Error) {
    setPopup({
      open: true,
      title: err.message,
      content: '',
      variant: 'error'
    });
  }

  const { lastElementRef } = useLastElementScroll(
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage
  );

  const flattenedData = useMemo(
    () => (infinityUsers ? infinityUsers?.pages.flatMap(item => item.data.data) : []),
    [infinityUsers]
  );

  const checkedMobileUsers = useMemo(
    () =>
      isMobile && flattenedData && deleteIds
        ? flattenedData.filter(row => deleteIds.includes(+row.id))
        : [],
    [deleteIds]
  );

  const checkedMobileUsersNames = useMemo(
    () =>
      isMobile && checkedMobileUsers ? checkedMobileUsers.map(obj => obj.name).join(', ') : [],
    [checkedMobileUsers]
  );

  const renderMobileUsers = () => {
    return flattenedData.map((option, index) => (
      <Box
        ref={flattenedData.length === index + 1 ? lastElementRef : null}
        key={index}
        width={'100%'}
      >
        <MobileUserRow
          key={index}
          index={index}
          data={option}
          openMobileUserDropdown={openMobileUserDropdown}
          handleToggleMobileDetails={handleToggleMobileDetails}
          handleClick={handleClick}
          isUserChecked={deleteIds}
          setIsUserChecked={setDeleteIds}
        />
      </Box>
    ));
  };

  const columnsAllUsers = useMemo<MRT_ColumnDef<User | {}>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name_and_surname'),
        minSize: 10,
        enableEditing: false,
        enableColumnOrdering: false
      },
      {
        accessorKey: 'phone',
        header: t('phone'),
        minSize: 10,
        enableEditing: false,
        enableColumnOrdering: false
      },
      {
        accessorKey: 'email',
        header: t('email'),
        minSize: 10,
        enableEditing: false,
        enableColumnOrdering: false
      },
      {
        accessorKey: 'date_of_birth',
        header: t('date_of_birth'),
        minSize: 10,
        enableEditing: false,
        enableColumnOrdering: false
      },
      {
        accessorKey: 'created_at',
        header: t('created_at'),
        minSize: 10,
        enableEditing: false,
        enableColumnOrdering: false
      }
    ],
    [t]
  );

  return (
    <>
      {!isMobile ? (
        <Grid container>
          {successfullyDeletedPopup && deleteIds.length > 1 ? (
            <Alert
              severity="success"
              sx={{ position: 'sticky', zIndex: 200, width: '95%', m: 'auto', mt: 2 }}
            >
              {t('users')}{' '}
              <span>
                {deletedNames.length > 30 ? deletedNames.slice(0, 30) + '...' : deletedNames}
              </span>{' '}
              {t('superAdminUsers.successMultiple')}
            </Alert>
          ) : successfullyDeletedPopup && deleteIds.length === 1 ? (
            <Alert
              severity="success"
              sx={{ position: 'sticky', zIndex: 200, width: '95%', m: 'auto', mt: 2 }}
            >
              {t('users')}{' '}
              <span>
                {deletedNames.length > 30 ? deletedNames.slice(0, 30) + '...' : deletedNames}
              </span>{' '}
              {t('superAdminUsers.successSingle')}
            </Alert>
          ) : null}
          <Card sx={{ mx: 3 }} className="fullWidth">
            <TableHeaderActions
              tableVariant="users"
              handleSearch={handleSearch}
              disableActions={!deletedNames}
              deleteEventHandler={checkPermissionFunc(
                currentCMS,
                PermissionsEnum.delete_sport_centers,
                deleteEventHandler
              )}
              //sort
              handleSortTables={handleSortTables}
              checkSort={checkSort}
              handleClearSort={handleClearSort}
              //filter
              handleClearFilter={handleClearFilter}
              handleClick={handleClick}
              handleClose={handleClose}
              anchorFilterEl={anchorEl}
              anchorElActionButton={anchorElActionButton}
              anchorMenuItem={anchorMenuItem}
              anchorSortEl={anchorSortEl}
              controlFilter={controlFilter}
              handleSubmitFilter={handleSubmitFilter}
              filterValue={filterValue}
            />
            <Grid item lg={12} ml={'20px'} mr={'20px'}>
              {data && !isLoadingUsers ? (
                <TableLayout
                  tableVariant="users"
                  rows={data?.data?.data}
                  columns={columnsAllUsers}
                  pagination={pagination}
                  setPagination={setPagination}
                  rowCount={data?.data?.meta?.total}
                  tableInstanceRef={tableInstanceRef}
                  withCheckbox
                />
              ) : (
                <Loading />
              )}
            </Grid>
          </Card>
        </Grid>
      ) : (
        <Grid container className="fullWidth" mt={-1}>
          <Grid
            item
            xs={12}
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            height={'80px'}
            borderBottom={'1px solid #D1D5DB'}
            position={'sticky'}
            top={0}
            sx={{ background: 'white' }}
            zIndex={2}
          >
            <MobileTableHeader
              tableVariant={'users'}
              setOpenMobileMenu={setOpenMobileMenu}
              handleSearch={handleSearch}
              filterValues={filterValues}
              handleClick={handleClick}
              handleClose={handleClose}
              anchorSortEl={anchorSortEl}
              handleSortTables={handleSortTables}
              checkSort={checkSort}
              handleClearSort={handleClearSort}
            />
          </Grid>
          {deleteIds.length >= 1 && (
            <Grid
              item
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              borderBottom={'1px solid #D1D5DB'}
              height={'56px'}
              sx={{ background: '#F5F7FA' }}
            >
              <Grid
                item
                xs={10}
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                ml={6}
              >
                <Typography fontSize={'16px'} fontWeight={500} ml={7.5}>
                  {checkedMobileUsersNames.toString().length > 30
                    ? checkedMobileUsersNames.toString().slice(0, 30) + '...'
                    : checkedMobileUsersNames.toString()}
                </Typography>
              </Grid>
              <Grid item xs={5} display={'flex'} justifyContent={'end'} flexDirection={'row'}>
                {/* <IconButton>
                  <MoreHorizIcon id="threeDots" data-testid="threeDots" sx={{ color: 'black' }} />
                </IconButton> */}
                <IconButton
                  sx={{
                    border: '1px solid #CACCCF',
                    borderRadius: '6px',
                    background: 'white',
                    mr: 2
                  }}
                  onClick={deleteEventHandler}
                >
                  <DeleteRowIcon id="deleteBulk" data-testid="deleteBulk" sx={{ color: 'black' }} />
                </IconButton>
              </Grid>
            </Grid>
          )}
          {isLoading ? <CircularProgress /> : renderMobileUsers()}
          <Menu
            id="singleActionBtn-menu"
            anchorEl={anchorElActionButton}
            open={anchorElActionButton !== null}
            onClose={handleClose}
          >
            {deleteEventHandler && (
              <MenuItem onClick={deleteEventHandler}>
                <DeleteRowIcon />
                <Typography ml={3.5}>{t('delete')}</Typography>
              </MenuItem>
            )}
          </Menu>
          <MobileMenu
            anchor="right"
            open={openMobileMenu}
            onClose={() => setOpenMobileMenu(false)}
          />
        </Grid>
      )}
    </>
  );
};

export default UsersPage;
